import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, XCircle, Package, Loader2, Clock, Edit2 } from 'lucide-react';
import { stockAdjustmentService } from '../../../../services/erpApi';
import { useToast } from '../../../../hooks/useToast';
import { usePermissions } from '../../../../hooks/usePermissions';
import { MODULES, ACTIONS } from '../../../../utils/permissions';
import { Toast } from '../../../../components';
import './StockAdjustmentDetailPage.css';

interface AdjustmentItem {
  id: number;
  product_id: number;
  product_name?: string;
  product_sku?: string;
  physical_stock: number;
  digital_stock: number;
  adjustment_quantity: number;
  notes?: string;
}

interface AdjustmentRequest {
  id: number;
  request_number: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  reason?: string;
  notes?: string;
  created_by?: number;
  created_by_name?: string;
  approved_by?: number;
  approved_by_name?: string;
  approved_at?: string;
  created_at: string;
  items?: AdjustmentItem[];
}

const StockAdjustmentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast();
  const { hasPermission } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [request, setRequest] = useState<AdjustmentRequest | null>(null);

  const canApprove = hasPermission(MODULES.STOCK, ACTIONS.STOCK_ADJUSTMENT_APPROVE);
  const canReject = hasPermission(MODULES.STOCK, ACTIONS.STOCK_ADJUSTMENT_REJECT);
  const canCreate = hasPermission(MODULES.STOCK, ACTIONS.STOCK_ADJUSTMENT_CREATE);
  const canEditRequest = canCreate || canApprove;

  const loadRequest = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await stockAdjustmentService.getById(id);
      if (response.success && response.data) {
        setRequest(response.data);
      } else {
        showToast('Pedido não encontrado', 'error');
        navigate('/erp/stock/adjustments');
      }
    } catch (error: any) {
      showToast('Erro ao carregar pedido', 'error');
      navigate('/erp/stock/adjustments');
    } finally {
      setLoading(false);
    }
  }, [id, showToast, navigate]);

  useEffect(() => {
    if (id) {
      loadRequest();
    }
  }, [id, loadRequest]);

  const handleApprove = async () => {
    if (!id || !request) return;
    
    if (!window.confirm('Tem certeza que deseja aprovar este pedido? O estoque será atualizado.')) {
      return;
    }

    setProcessing(true);
    try {
      const response = await stockAdjustmentService.approve(id);
      if (response.success) {
        showToast('Pedido aprovado e estoque atualizado com sucesso', 'success');
        loadRequest();
      } else {
        showToast(response.message || 'Erro ao aprovar pedido', 'error');
      }
    } catch (error: any) {
      showToast('Erro ao aprovar pedido', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!id || !request) return;
    
    const notes = window.prompt('Informe o motivo da rejeição:');
    if (notes === null) return;

    setProcessing(true);
    try {
      const response = await stockAdjustmentService.reject(id, notes);
      if (response.success) {
        showToast('Pedido rejeitado', 'success');
        loadRequest();
      } else {
        showToast(response.message || 'Erro ao rejeitar pedido', 'error');
      }
    } catch (error: any) {
      showToast('Erro ao rejeitar pedido', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'pending': return { label: 'Pendente', color: 'status-pending', icon: Clock };
      case 'approved': return { label: 'Aprovado', color: 'status-approved', icon: CheckCircle2 };
      case 'rejected': return { label: 'Rejeitado', color: 'status-rejected', icon: XCircle };
      case 'cancelled': return { label: 'Cancelado', color: 'status-cancelled', icon: XCircle };
      default: return { label: 'Desconhecido', color: 'status-default', icon: Clock };
    }
  };

  if (loading) {
    return (
      <div className="stock-adjustment-detail-loading">
        <Loader2 size={32} className="spinner" />
        <p>Carregando...</p>
      </div>
    );
  }

  if (!request) {
    return null;
  }

  const statusConfig = getStatusConfig(request.status);

  return (
    <div className="stock-adjustment-detail">
      <div className="stock-adjustment-detail-header">
        <button className="stock-adjustment-detail-back" onClick={() => navigate('/erp/stock/adjustments')}>
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1>{request.request_number}</h1>
          <div className="stock-adjustment-detail-status">
            <statusConfig.icon size={16} />
            <span className={statusConfig.color}>{statusConfig.label}</span>
          </div>
        </div>
      </div>

      <div className="stock-adjustment-detail-content">
        {/* Info Card */}
        <div className="stock-adjustment-detail-card">
          <h3>Informações do Pedido</h3>
          <div className="stock-adjustment-detail-info">
            <div>
              <label>Criado por</label>
              <p>{request.created_by_name || `Usuário #${request.created_by}`}</p>
            </div>
            <div>
              <label>Data de Criação</label>
              <p>{new Date(request.created_at).toLocaleString('pt-BR')}</p>
            </div>
            {request.approved_by_name && (
              <>
                <div>
                  <label>Aprovado por</label>
                  <p>{request.approved_by_name}</p>
                </div>
                <div>
                  <label>Data de Aprovação</label>
                  <p>{request.approved_at ? new Date(request.approved_at).toLocaleString('pt-BR') : '-'}</p>
                </div>
              </>
            )}
            {request.reason && (
              <div className="stock-adjustment-detail-full">
                <label>Motivo</label>
                <p>{request.reason}</p>
              </div>
            )}
            {request.notes && (
              <div className="stock-adjustment-detail-full">
                <label>Observações</label>
                <p>{request.notes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Items Card */}
        <div className="stock-adjustment-detail-card">
          <h3>Itens do Ajuste ({request.items?.length || 0})</h3>
          {request.items && request.items.length > 0 ? (
            <div className="stock-adjustment-detail-items">
              <table className="stock-adjustment-detail-table">
                <thead>
                  <tr>
                    <th>Produto</th>
                    <th className="text-center">Estoque Físico</th>
                    <th className="text-center">Estoque Digital</th>
                    <th className="text-center">Ajuste</th>
                  </tr>
                </thead>
                <tbody>
                  {request.items.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <p className="stock-adjustment-detail-item-name">{item.product_name}</p>
                        <p className="stock-adjustment-detail-item-sku">SKU: {item.product_sku || 'N/A'}</p>
                      </td>
                      <td className="text-center">{item.physical_stock}</td>
                      <td className="text-center">{item.digital_stock}</td>
                      <td className={`text-center ${item.adjustment_quantity > 0 ? 'positive' : item.adjustment_quantity < 0 ? 'negative' : ''}`}>
                        {item.adjustment_quantity > 0 ? '+' : ''}{item.adjustment_quantity}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="stock-adjustment-detail-empty">
              <Package size={48} />
              <p>Nenhum item encontrado</p>
            </div>
          )}
        </div>

        {/* Actions */}
        {request.status === 'pending' && (canEditRequest || canApprove || canReject) && (
          <div className="stock-adjustment-detail-actions">
            {canEditRequest && (
              <button
                type="button"
                onClick={() => navigate(`/erp/stock/adjustments/${id}/edit`)}
                className="stock-adjustment-detail-edit"
              >
                <Edit2 size={18} />
                Voltar para editar
              </button>
            )}
            {canReject && (
              <button onClick={handleReject} disabled={processing} className="stock-adjustment-detail-reject">
                <XCircle size={18} />
                Rejeitar
              </button>
            )}
            {canApprove && (
              <button onClick={handleApprove} disabled={processing} className="stock-adjustment-detail-approve">
                {processing ? <Loader2 size={18} className="spinner" /> : <CheckCircle2 size={18} />}
                {processing ? 'Processando...' : 'Aprovar e Atualizar Estoque'}
              </button>
            )}
          </div>
        )}
      </div>

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}
    </div>
  );
};

export default StockAdjustmentDetailPage;
